<script lang="ts">
  import { onMount, createEventDispatcher } from "svelte";
  import CustomIcon from "../CustomIcon.svelte";
  import SortableList from "../SortableList.svelte";
  import PageSizerPicker from "./PageSizerPicker.svelte";
  import {
    Config,
    DisplayConfigItem,
    configInfo,
    Column,
    Pagination,
  } from "./types.svelte";
  import RenderToggleButton from "../button/RenderToggleButton.svelte";
  import Form from "../form/Form.svelte";
  import InputWrapper from "../form/InputWrapper.svelte";
  import PlainInput from "../form/PlainInput.svelte";
  import GenericButton from "../button/GenericButton.svelte";

  const dispatch = createEventDispatcher();

  export let onCloseModal: () => void;
  export let onConfigChange: (config: Config) => void;
  export let isVisible = false;
  let config: Config;
  export let defaultConfig: Config;
  export let columns: Column[];
  export let onSortColumns: (e: any) => void;
  export let onColumnVisibilityChange: (key: string, isHidden: boolean) => void;
  export let pagination: Pagination;
  // export let onColumnsWidthChange: (e: any, item: Column) => void;
  // export let onResetColumnsWidth: () => void;
  // export let onToggleFitContent: (item: Column) => void;
  const defaultWidth = "110";
  if (!defaultConfig.columnWidth) {
    defaultConfig.columnWidth = defaultWidth;
  }

  let displayConfig: DisplayConfigItem[] = [];
  let columnsConfig: Column[] = [];

  function updateConfig(key: string, value: boolean) {
    config = { ...config, [key]: value };
    displayConfig = configInfo
      .filter((c) => c.configurable)
      .map((c) => ({ ...c, value: config?.[c.key] }));

    const mainSettingArray = JSON.parse(
    localStorage.getItem(`_tableMainSettings_${config.tableId}`)
    );

    localStorage.setItem(
      `_tableMainSettings_${config.tableId}`,
      JSON.stringify({...mainSettingArray, ...config})
    );
    onConfigChange(config);
  }

  function saveCustomDataToStorage() {
    const customSettingArray = []; //extract datakey and hidden values into and array for all columns
    columnsConfig.forEach((element) => {
      const { dataKey, isHidden, fitContent, width } = element;
      customSettingArray.push({
        dataKey,
        isHidden,
        fitContent,
        width,
      });
    });

    localStorage.setItem(
      `_tableCustomSettings_${config.tableId}`,
      JSON.stringify(customSettingArray)
    );
  }

  function onCustomSettingsSort(e) {
    columnsConfig = e.detail;
    updateColumns(columnsConfig);
    onSortColumns(e);
  }
  function updateColumns(columnsConfig) {
    saveCustomDataToStorage();
    dispatch("updateColumns", columnsConfig);
  }

  function updateCustomCheck(item) {
    const dataKey = item.dataKey;
    const isHidden = !item.isHidden;

    columnsConfig = columnsConfig.map((column) =>
      column.renderBy !== "actions" && column.dataKey === dataKey
        ? { ...column, isHidden }
        : column
    );
    updateColumns(columnsConfig);
    onColumnVisibilityChange(item.dataKey, !item.isHidden);
  }

  function toggleFitContent(item) {
    const dataKey = item.dataKey;
    const fitContent = !item.fitContent;

    columnsConfig = columnsConfig.map((column) => {
      if (column.dataKey == dataKey) {
        column.fitContent = fitContent;
        if (fitContent) {
          delete column.width;
        } else {
          column.width = config.columnWidth;
        }
        // return column;
      }
      return column;
    });

    updateColumns(columnsConfig);
    // onToggleFitContent(item);
  }

  function changeColumnsWidth(value, item) {
    const dataKey = item.dataKey;
    const width = Math.max(+value, 80);

    columnsConfig = columnsConfig.map((column) =>
      column.dataKey == dataKey ? { ...column, width } : column
    );

    updateColumns(columnsConfig);
    // onColumnsWidthChange(value, item);
  }

  function getColumnWidth(item) {
    return item.width ?? undefined;
  }

  function ResetColumnWidth() {
    columnsConfig = columnsConfig.map((column) => {
      return {
        ...column,
        width: defaultConfig.columnWidth,
        fitContent: false,
      };
    });
    updateColumns(columnsConfig);
    // onResetColumnsWidth();
  }

  onMount(() => {
    const mainSettingArray = JSON.parse(
      localStorage.getItem(`_tableMainSettings_${defaultConfig.tableId}`)
    );
    const customSettingArray = JSON.parse(
      localStorage.getItem(`_tableCustomSettings_${defaultConfig.tableId}`)
    );

    config = {...defaultConfig, ...mainSettingArray} ;
    columnsConfig = columns; //initialize columns

    if (customSettingArray !== null) {
      // if storage data is available sort the columns using the settings from storage.
      const sortedColumns = [];
      customSettingArray.forEach((customColumn) => {
        const index = columnsConfig.findIndex(
          (column) => column.dataKey === customColumn.dataKey
        );
        // check that the storage doesn't contain old columns
        if(columnsConfig[index]) sortedColumns.push(columnsConfig[index]);
      });

      columnsConfig = sortedColumns; // updated columns with sorted data
      columnsConfig = columnsConfig.map((column) => {
        const match = customSettingArray.find(
          (customColumn) => customColumn.dataKey === column.dataKey
        );
        if (match) {
          //update all fields with data from storage
          return {
            ...column,
            isHidden: match.isHidden,
            fitContent: match.fitContent,
            width: match.width,
          };
        }
        return column;
      });
    }

    displayConfig = configInfo
      .filter((c) => c.configurable)
      .map((c) => ({ ...c, value: config?.[c.key] }));
  });
</script>

{#if isVisible}
  <div
    class="fixed z-100 inset-0 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div
        on:click={onCloseModal}
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
      />

      <span
        class="hidden sm:inline-block sm:align-middle sm:h-screen"
        aria-hidden="true"
      >
        &#8203;
      </span>

      <div
        class="inline-block align-bottom bg-white rounded-lg pb-5 text-left overflow-hidden
        shadow-xl transform transition-all sm:align-middle w-full sm:max-w-690
        sm:w-full"
      >
        <!-- header -->
        <div
          class="bg-custom-light-orange py-4 px-6 flex flex-row justify-between items-center"
        >
          <h3 class="text-center font-bold">List Settings</h3>
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            on:click={onCloseModal}
            class="hover:text-gray-500 border-transparent cursor-pointer"
          >
            <span class="sr-only">Close</span>

            <CustomIcon width="18" height="18" name="close_dialog" />
          </div>
        </div>
        <div
          class="w-full h-5 flex justify-end pr-8 text-xs font-bold gap-8 pt-4 pb-4"
        >
          Column Width
          <GenericButton
            width="5rem"
            height="1rem"
            text="Reset"
            onClick={ResetColumnWidth}
          />
        </div>
        <!-- body -->
        <div class="px-6">
          <div>
            <SortableList
              list={columnsConfig}
              key="dataKey"
              on:sort={(e) => {
                onCustomSettingsSort(e);
              }}
              let:item
            >
              <div class="mt-2 mb-2">
                <div class="flex justify-between">
                  <div class="flex items-center">
                    <span class="cursor-move">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                        />
                      </svg>
                    </span>
                    <input
                      id={`column-visibility-${item.dataKey}`}
                      type="checkbox"
                      class="focus:ring-yellow-500 h-4 w-4 text-yellow-600 border-gray-300"
                      checked={!item.isHidden}
                      on:change={() => updateCustomCheck(item)}
                    />
                    <label
                      for={`column-visibility-${item.key}`}
                      class="ml-3 whitespace-nowrap block text-sm font-medium text-gray-700"
                    >
                      {item.label || item.key}
                    </label>
                  </div>
                  <div class="flex text-sm items-center">
                    <Form>
                      <InputWrapper label="">
                        <PlainInput
                          disabled={item.fitContent}
                          on:change={(e) =>
                            changeColumnsWidth(e.detail.target.value, item)}
                          name="fitContent"
                          small={true}
                          min={80}
                          isClearable={false}
                          step={1}
                          value={getColumnWidth(item)}
                          appendPrefix="px"
                          type="number"
                        />
                      </InputWrapper>
                    </Form>
                    <div class="mx-1 flex items-center gap-1">
                      <RenderToggleButton
                        active={item.fitContent}
                        click={() => toggleFitContent(item)}
                        colors={{ on: "#009834", off: "#333333" }}
                      /> Fit content
                    </div>
                  </div>
                </div>
              </div>
            </SortableList>
          </div>

          <div class="h-[1px] mt-8 mb-4 bg-gray-200" />

          {#each displayConfig as config}
            <div class="mt-6 mb-2 ml-6">
              <div class="flex items-center">
                <input
                  id={"header-setting_" + config.key}
                  type="checkbox"
                  class="focus:ring-yellow-500 h-4 w-4 text-yellow-600 border-gray-300"
                  checked={config.value}
                  on:change={() => updateConfig(config.key, !config.value)}
                />
                <label
                  for={"header-setting_" + config.key}
                  class="ml-3 whitespace-nowrap block text-sm font-medium text-gray-700"
                >
                  {config.label}
                </label>
              </div>
            </div>
          {/each}

          <div class="h-[1px] mt-8 mb-4 bg-gray-200" />
          <div class="mb-4 ml-4 flex flex-row items-center gap-4">
            <PageSizerPicker on:pageChange {pagination} shouldShowlabel />
          </div>
        </div>
        <!-- footer -->
      </div>
    </div>
  </div>
{/if}
